import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription, BehaviorSubject } from 'rxjs';
import { UserDataService } from '../user-data.service';

@Injectable({
  providedIn: 'root'
})
export class OsNotificationService implements OnDestroy {
  private permission: NotificationPermission = 'default';
  private notificationSubject = new Subject<string>();
  notificationStatus$ = this.notificationSubject.asObservable();

  // Show notifications
  private _enableBrowserNotifications = new BehaviorSubject<boolean>(false);
  enableBrowserNotifications$ = this._enableBrowserNotifications.asObservable();

  // Show assigned inq
  private _enableBrowserNotificationsAssigned = new BehaviorSubject<boolean>(false);
  enableBrowserNotificationsAssigned$ = this._enableBrowserNotificationsAssigned.asObservable();

  // Show new inq
  private _enableBrowserNotificationsNew = new BehaviorSubject<boolean>(false);
  enableBrowserNotificationsNew$ = this._enableBrowserNotificationsNew.asObservable();

  private notificationSubscription: Subscription;

  // private localStorageKey = 'enableBrowserNotifications';

  constructor(private userDataService: UserDataService) {
    // initialize permission from the browser
    if ('Notification' in window) {
      this.permission = Notification.permission;
    }

    // load from local data
    this.loadFromLocalData()

    // load from localstorage
    // this.loadFromLocalStorage();
  }

  // update the BehaviorSubject
  setEnableBrowserNotifications(isEnabled: boolean) {
    this._enableBrowserNotifications.next(isEnabled);
    // this.saveToLocalStorage(isEnabled);
  }

  getNotificationState():boolean{
      return this._enableBrowserNotifications.value;
  }

  // Storing the variable in localstorage, if needed.
  // private saveToLocalStorage(isEnabled: boolean) {
  //   localStorage.setItem(this.localStorageKey, JSON.stringify(isEnabled));
  // }

  // loadFromLocalStorage() {
  //   const storedValue = localStorage.getItem(this.localStorageKey);
  //   if (storedValue !== null) {
  //     this._enableBrowserNotifications.next(JSON.parse(storedValue));
  //   }
  // }


  loadFromLocalData() {
    this.notificationSubscription = this.userDataService.LocalGetLoginUserInfo().subscribe(loginUserInfo => {
      const assignedInq = loginUserInfo?.notificationSettings?.notificationChecker_AssignInq ?? false;
      const newInq = loginUserInfo?.notificationSettings?.notificationChecker_NewInq ?? false;

      if (assignedInq || newInq) {
        this._enableBrowserNotifications.next(true);
        
      } else {
        this._enableBrowserNotifications.next(false);
      }

      this.getShowAssignedInqNotification(assignedInq);
      this.getShowNewInqNotification(newInq);
    });
  }

  getShowAssignedInqNotification(assignedInq) {
    if(assignedInq === true) {
      this._enableBrowserNotificationsAssigned.next(true);
    } else {
      this._enableBrowserNotificationsAssigned.next(false);
    }
  }

  getShowNewInqNotification (newInq) {
    if(newInq === true) {
      this._enableBrowserNotificationsNew.next(true);
    } else {
      this._enableBrowserNotificationsNew.next(false);
    }
  }
  

  ngOnDestroy() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
        return 'denied'; // or throw an error
    }
    try {
      this.permission = await Notification.requestPermission();
      this.notificationSubject.next(this.permission); // Notify subscribers
      return this.permission;
    } catch (error) {
      return 'denied';
    }
  }

  generateNotificationMessage(inq: any): string {
    const inquiryId = inq?._id;

    // latest audit entry.
    const latestAudit = inq?.workflowAuditLimitedToRecentFive && inq.workflowAuditLimitedToRecentFive.length > 0
      ? inq.workflowAuditLimitedToRecentFive[inq.workflowAuditLimitedToRecentFive.length - 1]
      : null;

    // Get the action + subject by prioritizing the latest audit
    const action = latestAudit?.action || inq?.action || 'Update';
    const subject = inq?.subject || 'No Subject';

    // Get the user: prioritize audit trail, then inquiry, then default.
    const latestUser = latestAudit?.modBy || inq?.latestUserName || 'System';

    // Actions from latestAudit
    if (latestAudit) {
      switch (latestAudit.action) {
        case "Assign Owner":
          return `Inquiry ${inquiryId}: Ownership assigned to you by ${latestAudit.modBy}`;
        case "Re Age Inquiry":
          return `Inquiry ${inquiryId}: Re-aged by ${latestAudit.modBy}`;
        case "Resolve":
          return `Inquiry ${inquiryId}: Resolved by ${latestAudit.modBy}`;
        case "Approve":
          return `Inquiry ${inquiryId}: Approved by ${latestAudit.modBy}`;
        case "ReplyAll":
          return `Inquiry ${inquiryId}: Replied to by ${latestAudit.modBy}`;
        case "Reopen":
          return `Inquiry ${inquiryId}: Reopened by ${latestAudit.modBy}`;
        case "De-escalate":
          return `Inquiry ${inquiryId}: De-escalated by ${latestAudit.modBy}`;
        case "Mark as Non-Inquiry":
          return `Inquiry ${inquiryId}: Marked as Non-Inquiry by ${latestAudit.modBy}`;
        case "Lock":
            return `Inquiry ${inquiryId}: Locked by ${latestAudit.modBy}`;
        case "Unlock":
            return `Inquiry ${inquiryId}: Unlocked by ${latestAudit.modBy}`;
        case "New Inquiry":
          return `Inquiry ${inquiryId}: New Inquiry created by ${latestAudit.modBy}`;
      }
    }

    // Fallback to workflows if there's no audit trail.
    if (inq?.workflows && inq.workflows.length > 0) {
      const latestWorkflow = inq.workflows[inq.workflows.length - 1];
      const workflowUser = latestWorkflow.modBy || latestUser; // Use workflow modBy if available, else the overall latestUser
      switch (latestWorkflow.action) {
          case "Assign Owner":
              return `Inquiry ${inquiryId}: Ownership assigned to you by ${workflowUser}`;
          case "Snooze":
              return `Inquiry ${inquiryId}: Snoozed by ${workflowUser}`;
          case "UnSnooze":
              return `Inquiry ${inquiryId}: Unsnoozed by ${workflowUser}`;
          case "FollowUp":
              return `Inquiry ${inquiryId}: Follow Up by ${workflowUser}`;
          case "Re Age Inquiry":
              return `Inquiry ${inquiryId}: Re-aged by ${workflowUser}`;
          case "Resolve":
              return `Inquiry ${inquiryId}: Resolved by ${workflowUser}`;
          case "Approve":
              return `Inquiry ${inquiryId}: Approved by ${workflowUser}`;
          case "ReplyAll":
              return `Inquiry ${inquiryId}: Replied to by ${workflowUser}`;
          case "Reopen":
              return `Inquiry ${inquiryId}: Reopened by ${workflowUser}`;
          case "De-escalate":
              return `Inquiry ${inquiryId}: De-escalated by ${workflowUser}`;
          case "Mark as Non-Inquiry":
              return `Inquiry ${inquiryId}: Marked as Non-Inquiry by ${workflowUser}`;
          case "Lock":
              return `Inquiry ${inquiryId}: Locked by ${workflowUser}`;
          case "Unlock":
              return `Inquiry ${inquiryId}: Unlocked by ${workflowUser}`;
          case "Ownership Nomination":
              return `Inquiry ${inquiryId}: Ownership nominated by ${workflowUser}`;
          case "New Inquiry":
              return `Inquiry ${inquiryId}: New Inquiry created by ${workflowUser}`;
          default: // Fallback within workflow
              return `Inquiry ${inquiryId}: ${latestWorkflow.action} by ${workflowUser}`;
      }
    }

    // ultimate Fallback Message  
    return `Inquiry ${inquiryId}: ${action} - ${subject} by ${latestUser}`;
  }

  showNotification(title: string, options: NotificationOptions = {}, inqId = 1, delay: number = 0): void { // using the BehaviorSubject's value directly
    if (!this._enableBrowserNotifications.value) {
      return;
    }

    if (!('Notification' in window)) {
      return;
    }

    /* Disabling the forced permission check for now */
    // if (this.permission !== 'granted') {
    //   // request permission again IFF notifications are enabled.
    //     if (this._enableBrowserNotifications.value) {
    //       this.requestPermission();
    //     }
    //   return;
    // }

    try {
      const notification = new Notification(title, {
        body: options.body || '',
        icon: options.icon || '',
        tag: `inquiry-${inqId}`,
        requireInteraction: true,
        lang: 'en-US',
        badge: '/qwyn-icon.png',
        ...options // spread any other provided options
      });

      notification.onclick = () => {
        window.focus(); // bring focus back to the browser window
        notification.close();
      };

      notification.onerror = (event) => {
        // For testing
        console.error('QMA-1579 Notification API error:', event);
      };

    } catch (error) {
      // For testing
      console.error('QMA-1579 - Error showing notification:', error);
    }
  }
}